import {
  Component,
  EventEmitter,
  Inject,
  Input,
  LOCALE_ID,
  OnChanges,
  Output,
} from '@angular/core';

import { ReadingIntentionsKey } from '®shared/annotations';
import { BibliographicResourceType } from '®shared/bibliographic-resource';
import { AppConfig } from '®shared/config';
import {
  PublishListAction,
  PublishListAndRequestReviewAction,
} from '®shared/container-structure/state';
import {
  Permissions,
  UserPermissions,
  hasPermissionForListId,
} from '®shared/permissions';
import { EditableList, ReadingListDetails } from '®shared/reading-list/models';
import { QuickFilter } from '®shared/reading-list/quick-filters';
import { DigitisationFilterStatus } from '®shared/reading-list/quick-filters/digitisation-status-filter-and-label';
import { ResourceTypeFilter } from '®shared/reading-list/resource-type-filter';
import { RequestReviewShowModalAction } from '®shared/reviews/request-review';
import { PeriodStatus } from '®shared/tenant';
import { UriService } from '®shared/url-utils';

/**
 * Component offering list alerts
 */
@Component({
  selector: 'rl-list-alerts',
  templateUrl: './list-alerts.component.html',
})
export class ListAlertsComponent implements OnChanges {
  DigitisationFilterStatus = DigitisationFilterStatus;
  ReadingIntentionsKey = ReadingIntentionsKey;
  BibliographicResourceType = BibliographicResourceType;

  @Input() filterText?: string;
  @Input() quickFilter?: QuickFilter;
  @Input() resourceTypeFilter?: ResourceTypeFilter;
  @Input() hasVisibleEntries?: boolean;
  @Input() hasCitationErrors?: boolean;
  @Input() queueHasPublishAction?: boolean;
  @Input() list: ReadingListDetails;
  @Input() editableList: EditableList;
  @Input() currentUserPermissions: UserPermissions;
  @Output() clearQuickFilter: EventEmitter<any> = new EventEmitter();
  @Output() clearResourceTypeFilter: EventEmitter<any> = new EventEmitter();
  @Output() clearTextFilter: EventEmitter<any> = new EventEmitter();
  @Output() publishList: EventEmitter<PublishListAction> = new EventEmitter();
  @Output() showRequestReviewModal: EventEmitter<RequestReviewShowModalAction> =
    new EventEmitter();
  @Output()
  showRequestReviewModalAndPublishList: EventEmitter<PublishListAndRequestReviewAction> =
    new EventEmitter();

  hasTimePeriodExpired = false;
  hasTimePeriodNotStarted = false;
  isRolledOverListAdmin = false;
  reviewWorkflow = this.appConfig.tenant.featureFlags.reviewWorkflow;

  private now = new Date();

  constructor(
    @Inject(LOCALE_ID) public locale: string,
    private appConfig: AppConfig,
    private uriService: UriService,
  ) {}

  getListUrl(listId: string): string {
    return this.uriService.getListViewUrl(listId);
  }

  ngOnChanges() {
    if (this.editableList.period) {
      const periodStatus = this.editableList.period.status(this.now);
      this.hasTimePeriodExpired = periodStatus === PeriodStatus.PAST;
      this.hasTimePeriodNotStarted = periodStatus === PeriodStatus.FUTURE;
    } else {
      this.hasTimePeriodExpired = false;
      this.hasTimePeriodNotStarted = false;
    }

    if (this.currentUserPermissions) {
      if (this.list.rolled_over_to) {
        const listAdminPermissions = [
          Permissions.EDIT_LIST,
          Permissions.PUBLISH_LIST,
        ];

        this.isRolledOverListAdmin = listAdminPermissions.some(permission =>
          hasPermissionForListId(
            permission,
            this.list.rolled_over_to.id,
            this.currentUserPermissions,
          ),
        );
      }
    }
  }
}
